import {
  FETCH_USER_AUTOFILL_SETTINGS,
  UPDATE_USER_AUTOFILL_SETTINGS,
  UPDATE_USER_TIMEZONE_SETTINGS,
  FETCH_USER_SETTINGS,
  FETCH_USER_SETTINGS_FAILURE,
  UPDATE_USER_ADDRESS_SEARCH_RADIUS_SETTINGS,
  UPDATE_USER_MAP_CENTER_SETTINGS,
  UPDATE_USER_ALGORITHM_CONFIDENCE_SETTINGS,
  UPDATE_USER_SELECTED_DEVICE,
  SET_MAP_CENTER,
  FETCH_ORGANIZATIONS,
  DECREMENT_ORG_UNREAD_MAINTENANCE_COUNT,
  UPDATE_SUPER_USER_ORGANIZATION,
  // RELOAD_SOCKET,
  FETCH_USERS_BY_ORG,
  FETCH_ALL_USERS,
  UPDATE_USER_PERMISSIONS,
  DELETE_USER,
} from "./types";

import {
  displaySuccessNotification,
  displayErrorNotification,
} from "./notifications";

export const updateUserAutofillSettings =
  (sendRequest, settings) => async (dispatch) => {
    dispatch({ type: UPDATE_USER_AUTOFILL_SETTINGS, payload: settings });
    await sendRequest("patch")({
      url: `/user/autofill-settings`,
      devPort: 5004,
      data: settings,
    });
  };

export const fetchUserAutofillSettings =
  (sendRequest, settings) => async (dispatch) => {
    dispatch({ type: FETCH_USER_AUTOFILL_SETTINGS, payload: settings });
    const response = await sendRequest("get")({
      url: `/user/autofill-settings`,
      devPort: 5004,
    });
    dispatch({ type: FETCH_USER_AUTOFILL_SETTINGS, payload: response.data });
  };

export const updateUserTimeZoneSettings = (timeZone) => async (dispatch) => {
  dispatch({
    type: UPDATE_USER_TIMEZONE_SETTINGS,
    payload: { timeZone: timeZone },
  });
};

export const updateUserAddressSearchRadiusSettings =
  (addressSearchRadius) => async (dispatch) => {
    dispatch({
      type: UPDATE_USER_ADDRESS_SEARCH_RADIUS_SETTINGS,
      payload: { addressSearchRadius },
    });
  };

export const updateUserAlgConfidenceSettings =
  (algConfidence) => async (dispatch) => {
    dispatch({
      type: UPDATE_USER_ALGORITHM_CONFIDENCE_SETTINGS,
      payload: { algConfidence },
    });
  };

export const updateUserMapCenterSettings = (mapCenter) => async (dispatch) => {
  dispatch({ type: UPDATE_USER_MAP_CENTER_SETTINGS, payload: { mapCenter } });
  let mapLocation = mapCenter?.split(",")?.map(parseFloat);

  dispatch({ type: SET_MAP_CENTER, payload: mapLocation });
};

export const updateUserSelectedDevice = (selectedDevice) => async (dispatch) => {
  dispatch({ type: UPDATE_USER_SELECTED_DEVICE, payload: { selectedDevice } });
}

// AppRouter fires this on mount without awaiting it, so anything thrown here
// used to leave an unhandled rejection and nothing else: no organization in the
// store, and no record that the request had even finished. PrivateRoute reads
// both, and with neither it rendered nothing at all - a blank white page as the
// app's response to being refused.
//
// A refusal is an answer, so dispatch it. 403 is the expected one: the backend
// returns it when it cannot resolve an organization for the caller (ORG_MISSING
// when the token carries no membership for this product, which is what a token
// cached from before a membership was granted looks like).
export const fetchUserSettings =
  (sendRequest, settings) => async (dispatch) => {
    try {
      const response = await sendRequest("get")({
        url: `/user/user-settings`,
        devPort: 5004,
      });
      dispatch({ type: FETCH_USER_SETTINGS, payload: response.data });
    } catch (err) {
      dispatch({
        type: FETCH_USER_SETTINGS_FAILURE,
        payload: {
          status: err?.response?.status,
          reason: err?.response?.data?.error,
        },
      });
    }
  };

// Goes over sendOrganizationRequest rather than sendRequest: the organization
// service reads camelCase query params, and sendRequest snake-cases every key on
// its way out. Sent that way includeUnreadCounts arrives as include_unread_counts,
// which the endpoint ignores - so the list comes back with no counts on it and
// every unread badge reads zero, with no error anywhere to say why.
export const fetchOrganizations =
  (sendOrganizationRequest, { includeUnreadCounts, env, background } = {}) =>
  async (dispatch, getState) => {
    const countsVersions = background
      ? getState()?.user?.unreadCountsVersions
      : undefined;
    try {
      const params = includeUnreadCounts ? { includeUnreadCounts } : undefined;
      if (params && env) {
        params.env = env;
      }
      const response = await sendOrganizationRequest("get")({
        url: "/organization/organizations",
        ...(params ? { params } : {}),
      });
      dispatch({
        type: FETCH_ORGANIZATIONS,
        payload: { ...response.data, countsVersions },
      });
    } catch (err) {
      if (!background) {
        throw err;
      }
    }
  };

export const decrementOrgUnreadMaintenanceCount =
  (organizationId, amount) => async (dispatch) => {
    dispatch({
      type: DECREMENT_ORG_UNREAD_MAINTENANCE_COUNT,
      payload: { organizationId, amount },
    });
  };

export const updateSuperUserOrganization =
  (sendRequest, organizationUuid, getToken) => async (dispatch) => {
    dispatch({
      type: UPDATE_SUPER_USER_ORGANIZATION,
      payload: { organizationUuid },
    });
    const res = await sendRequest("patch")({
      url: `/user/superuser/organization`,
      devPort: 5004,
      data: { organization: organizationUuid },
    }).catch((err) => {
      console.log("Error in PATCH /user/superuser/organization");
    });
    console.log(res);
    if (res?.status === 200) {
      await getToken(true);
      window.location.reload(false);
      // dispatch({
      //   type: RELOAD_SOCKET,
      //   socket: { organizationRefresh: true, organization: organizationUuid },
      // });
    }
  };

export const getUsersByOrg =
  (sendRequest, organization) => async (dispatch) => {
    const res = await sendRequest("get")({
      url: `/user/superuser/organization/${organization}/users`,
      devPort: 5004,
    }).then((res) =>
      res?.status === 200
        ? res.data.reduce(
          (curr, user) => ({ ...curr, [user.user_id]: user }),
          {}
        )
        : null
    );
    if (res) {
      dispatch({
        type: FETCH_USERS_BY_ORG,
        payload: { orgUsers: { [organization]: res } },
      });
    }
  };

/**
 * Every user, with their primary organization and IDX memberships.
 *
 * Fetched whole rather than per organization because the panel needs a count for
 * every organization at once, and because "unaffiliated" now means "no
 * organization OR one that IDX does not have" - which cannot be asked of Auth0,
 * only worked out against the organization list.
 */
export const fetchAllUsers = (sendRequest) => async (dispatch) => {
  const res = await sendRequest("get")({
    url: `/user/superuser/users`,
    devPort: 5004,
  }).catch(() => {
    dispatch(displayErrorNotification("Failed to load users"));
  });

  if (res?.status === 200) {
    dispatch({
      type: FETCH_ALL_USERS,
      payload: {
        users: res.data?.users || [],
        truncated: !!res.data?.truncated,
      },
    });
    if (res.data?.truncated) {
      dispatch(
        displayErrorNotification(
          "Showing the first 1000 users only; the directory is larger."
        )
      );
    }
  }
};

/**
 * Replace a user's IDX organizations.
 *
 * Supersedes approveUser: granting an unaffiliated user their first organization
 * is the same operation as adding a second to somebody who already has one, so
 * there is no longer a separate "approve".
 *
 * Role and product are fixed server-side; only the organizations are sent.
 */
export const updateUserPermissions =
  (sendRequest, userId, organizations, userName) => async (dispatch) => {
    const res = await sendRequest("put")({
      url: `/user/superuser/user/${userId}/permissions`,
      devPort: 5004,
      data: { organizations },
    }).catch(() => {
      dispatch(
        displayErrorNotification(`Failed to update permissions for ${userName}`)
      );
    });

    if (res?.status === 200) {
      // Applied from the response, NOT by re-listing. GET /users reads Auth0's
      // search index, which is eventually consistent: a refetch here returns the
      // pre-write state for a few seconds and looks exactly like the save having
      // done nothing. The PUT response carries the updated user straight from
      // the write, including the primary organization the server derived, so
      // there is nothing to guess at either.
      dispatch({
        type: UPDATE_USER_PERMISSIONS,
        payload: { user: res.data?.user },
      });
      dispatch(
        displaySuccessNotification(`Updated permissions for ${userName}`)
      );
    }
  };

export const deleteUser =
  (sendRequest, organization, userId, userName) => async (dispatch) => {
    const res = await sendRequest("delete")({
      url: `/user/superuser/${userId}`,
      devPort: 5004,
    }).catch((err) => {
      dispatch(displayErrorNotification(`Failed to delete ${userName}`));
    });
    if (res?.status === 200) {
      dispatch({
        type: DELETE_USER,
        payload: {
          organization,
          userId,
        },
      });
      dispatch(displaySuccessNotification(`Successfully deleted ${userName}`));
    }
  };
